/**
 * 지사 메인( branch home ) 전용: ABOUT 섹션 악수·텍스트 등장 모션.
 * GSAP / ScrollTrigger 는 레이아웃에서 CDN 글로벌로 로드됩니다.
 *
 * 위치 조절: `_main.scss` 의 `.no-main__about-handshake`
 *   - 끝 지점(정지 시 겹침): `--about-handshake-{left|right}-final-{x|y}`
 *   - 시작 지점(모션 경로만): `--about-handshake-*-start-*`
 * 우선순위: CSS 변수 → img data-final-* → 기본값(시작은 JS 기본, 끝은 0).
 * 단위: px, %, vw 등 GSAP transform 이 받는 문자열
 *
 * 애니 직후: 인라인 transform 을 지우고 `--settled` 상태로 두어, 끝 위치를 CSS 변수로만
 * 유지한다. (그래야 개발자 도구에서 --about-handshake-*-final-* 수정 시 즉시 반영됨)
 *
 * 순서: 양손 모션·정지 → (가운데 심볼 있을 때만) 심볼 → `data-about-reveal` 텍스트 단계(손 모션과 겹치지 않음).
 */

function readCssVar(el, name) {
  if (!el) {
    return "";
  }
  return getComputedStyle(el).getPropertyValue(name).trim();
}

function readHandshakeStarts(handshakeEl) {
  const defaults = {
    left: { x: "-55%", y: "42%" },
    right: { x: "55%", y: "-35%" },
  };
  if (!handshakeEl) {
    return defaults;
  }

  const pick = (cssName, dataName, fallback) => {
    const fromCss = readCssVar(handshakeEl, cssName);
    if (fromCss.length) {
      return fromCss;
    }
    const fromData = handshakeEl.getAttribute(dataName);
    if (fromData !== null && fromData !== "") {
      return fromData;
    }
    return fallback;
  };

  return {
    left: {
      x: pick(
        "--about-handshake-left-start-x",
        "data-left-start-x",
        defaults.left.x,
      ),
      y: pick(
        "--about-handshake-left-start-y",
        "data-left-start-y",
        defaults.left.y,
      ),
    },
    right: {
      x: pick(
        "--about-handshake-right-start-x",
        "data-right-start-x",
        defaults.right.x,
      ),
      y: pick(
        "--about-handshake-right-start-y",
        "data-right-start-y",
        defaults.right.y,
      ),
    },
  };
}

function readHandshakeEnds(handshakeEl, leftEl, rightEl) {
  const zero = { x: "0", y: "0" };
  if (!handshakeEl) {
    return { left: zero, right: zero };
  }

  const pickPair = (which, imgEl) => {
    const xCss = readCssVar(
      handshakeEl,
      `--about-handshake-${which}-final-x`,
    );
    const yCss = readCssVar(
      handshakeEl,
      `--about-handshake-${which}-final-y`,
    );
    const xData = imgEl?.getAttribute("data-final-x");
    const yData = imgEl?.getAttribute("data-final-y");
    return {
      x:
        xCss.length > 0
          ? xCss
          : xData !== null && xData !== ""
            ? xData
            : zero.x,
      y:
        yCss.length > 0
          ? yCss
          : yData !== null && yData !== ""
            ? yData
            : zero.y,
    };
  };

  return {
    left: pickPair("left", leftEl),
    right: pickPair("right", rightEl),
  };
}

function applyHandshakeSettledFromCss(handshakeEl, leftEl, rightEl) {
  if (!handshakeEl || !leftEl || !rightEl) {
    return;
  }
  handshakeEl.classList.add("no-main__about-handshake--settled");
  gsap.set([leftEl, rightEl], { clearProps: "transform" });
}

export function initMainHomeAbout() {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    return;
  }

  const section = document.querySelector("[data-main-about]");
  if (!section) {
    return;
  }

  const handshakeEl = section.querySelector(".no-main__about-handshake");
  const starts = readHandshakeStarts(handshakeEl);

  const left = section.querySelector(".no-main__about-img--left");
  const right = section.querySelector(".no-main__about-img--right");
  const symbol = section.querySelector(".no-main__about-symbol");
  const reveals = section.querySelectorAll("[data-about-reveal]");

  if (!left || !right) {
    return;
  }

  const ends = readHandshakeEnds(handshakeEl, left, right);

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (reduceMotion) {
    applyHandshakeSettledFromCss(handshakeEl, left, right);
    gsap.set([left, right], { opacity: 1 });
    if (symbol) {
      gsap.set(symbol, { opacity: 1, scale: 1 });
    }
    gsap.set(reveals, { opacity: 1, y: 0 });
    return;
  }

  gsap.set(left, {
    x: starts.left.x,
    y: starts.left.y,
    opacity: 0.98,
  });
  gsap.set(right, {
    x: starts.right.x,
    y: starts.right.y,
    opacity: 0.98,
  });
  if (symbol) {
    gsap.set(symbol, { opacity: 0, scale: 0.88 });
  }
  gsap.set(reveals, { opacity: 0, y: 32 });

  const handDur = 1.6;
  const handLag = 0.08;
  const pauseAfterHandsBeforeText = 0.2;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: "top 55%",
      end: "bottom top",
      toggleActions: "play none none none",
    },
  });

  tl.to(
    left,
    {
      x: ends.left.x,
      y: ends.left.y,
      opacity: 1,
      duration: handDur,
      ease: "power2.out",
    },
    0,
  );
  tl.to(
    right,
    {
      x: ends.right.x,
      y: ends.right.y,
      opacity: 1,
      duration: handDur,
      ease: "power2.out",
    },
    handLag,
  );

  tl.add(() => {
    applyHandshakeSettledFromCss(handshakeEl, left, right);
  }, handLag + handDur);

  if (symbol) {
    tl.to(
      symbol,
      {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: "power2.out",
      },
      `>+=${pauseAfterHandsBeforeText * 0.5}`,
    );
  }

  tl.to(
    reveals,
    {
      opacity: 1,
      y: 0,
      duration: 0.72,
      stagger: 0.09,
      ease: "power2.out",
    },
    symbol ? ">+=0.1" : `>+=${pauseAfterHandsBeforeText}`,
  );

  requestAnimationFrame(() => {
    ScrollTrigger.refresh();
  });
}
